import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import {
  ScatterPlotData,
  defaultMetaData,
} from 'redux/slices/types'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { getScatterPlotDataFromDataFrame } from './getScatterPlotDataFromCellInfo'
import updateWithVisibleDensityColors from './updateWithVisibleDensityColors'
import { useBaseCellPlotData } from './useBaseCellPlotData'
import { getMetadataFromDataFrame } from './utils'
import { getCellInfoToGroupsFromDataFrame } from '../utils'
import { CATEGORICAL_COLOR_PALETTE } from '../shared'

// Storing the scatter plot data in redux (apparently?) doesn't work, so we have to resort to react context
// Storing and updating the scatterplot data all in one place should speed things up
// https://stackoverflow.com/questions/76126033/how-to-use-redux-with-react-plotly-js

const getDefaultScatterState = () =>
  ({
    plotData: [],
    cellInfo: [],
    scatterPlotMetaData: { ...defaultMetaData },
  } as ScatterPlotData)

export const PlotDataContext = createContext<ReturnType<typeof usePlotData>>(
  getDefaultScatterState()
)

export const PlotDataContextProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const { isPreFilter } = useCellVisualizationUrlParams()

  const [scatterPlotData, setScatterPlotData] = useState<ScatterPlotData>(getDefaultScatterState())
  const [plotMetadata, setPlotMetadata] = useState<typeof defaultMetaData>({ ...defaultMetaData })

  const {
    dataFrame,
    prefilteredCellIdSet,
    hiddenCellInfoGroupValues,
    cellVisualizations: { plotDisplayType, morphotypesToRemove, selectedCellInfoGroupName, cellInfoGroups },
    visibleSelectedComparisonDimensions,
    setCellInfoGroups,
  } = useCellVisualizationsSlice()

  const { getBaseCellPlotData: getBasePlot } = useBaseCellPlotData()

  // Optimize setCellInfoGroups calls to prevent unnecessary re-renders
  const setCellInfoGroupsOptimized = useCallback((newGroups: ReturnType<typeof getCellInfoToGroupsFromDataFrame>) => {
    setCellInfoGroups(newGroups)
  }, [setCellInfoGroups])

  // Extract cell info groups from DataFrame (memory-efficient)
  // In main visualization mode, exclude prefiltered cells from cellInfoGroups
  // In pre-filter mode, include all cells
  useEffect(() => {
    if (!dataFrame) return
    setCellInfoGroupsOptimized(
      getCellInfoToGroupsFromDataFrame(dataFrame, undefined, prefilteredCellIdSet, isPreFilter)
    )
  }, [dataFrame, prefilteredCellIdSet, isPreFilter, setCellInfoGroupsOptimized])

  // Stage 1: Process metadata from DataFrame (memory-efficient)
  useEffect(() => {
    if (!dataFrame) return
    const metaData = getMetadataFromDataFrame(dataFrame, isPreFilter ? undefined : morphotypesToRemove)
    setPlotMetadata(metaData)
  }, [dataFrame, isPreFilter, morphotypesToRemove])

  // Memoize number of columns and whether to calculate density per subplot
  // Only recalculate when the COUNT or PRESENCE of dimensions changes, not their order
  const dimensionCount = visibleSelectedComparisonDimensions?.length ?? 0
  const valueCount = visibleSelectedComparisonDimensions?.[0]?.values.length

  const numOfColumns = useMemo(() => valueCount, [valueCount])

  const shouldCalculateDensityPerSubplot = useMemo(() => dimensionCount > 0, [dimensionCount])

  // Stage 2: Generate plot data directly from DataFrame (memory-efficient)
  // This step depends on current plot layout settings (e.g. plot type, coloring method, etc.)
  useEffect(() => {
    if (!dataFrame) return

    const latestPlotData = getScatterPlotDataFromDataFrame({
      scatterPlotMetaData: plotMetadata,
      dataFrame,
      prefilteredCellIdSet,
      hiddenCellInfoGroupValues,
      selectedCellInfoGroupName,
      cellInfoGroups,
      categoricalColorPalette: CATEGORICAL_COLOR_PALETTE,
      getBasePlot,
      visibleSelectedComparisonDimensions,
      numOfColumns,
    })

    if (plotDisplayType === 'density') {
      updateWithVisibleDensityColors(latestPlotData.plotData, shouldCalculateDensityPerSubplot)
    }

    setScatterPlotData({
      ...latestPlotData,
      scatterPlotMetaData: plotMetadata,
    })
  }, [
    plotMetadata,
    dataFrame,
    prefilteredCellIdSet,
    hiddenCellInfoGroupValues,
    selectedCellInfoGroupName,
    cellInfoGroups,
    plotDisplayType,
    numOfColumns,
    shouldCalculateDensityPerSubplot,
    visibleSelectedComparisonDimensions,
    getBasePlot,
  ])

  return <PlotDataContext.Provider value={scatterPlotData}>{children}</PlotDataContext.Provider>
}

export const usePlotData = (): ScatterPlotData => {
  const data = useContext(PlotDataContext)
  return data
}
